import React, { useRef, useEffect, useState, useCallback } from "react";
import SignatureCanvasLib from "react-signature-canvas";
import { TEXTS } from "../../constants";
import { PenIcon, TrashIcon, MouseIcon } from "../icons";
import {
  SignatureCanvasWrapper,
  SignatureHeader,
  SignatureTitle,
  SignatureStatus,
  SignaturePad,
  SignaturePlaceholder,
  PlaceholderIcon,
  PlaceholderText,
  PlaceholderHint,
  SignatureFooter,
  FooterHint,
  ClearButton,
} from "./SignatureCanvas.styles";

function SignatureCanvas({ onEnd, onClear }) {
  const sigCanvas = useRef(null);
  const containerRef = useRef(null);
  const [isEmpty, setIsEmpty] = useState(true);
  const [canvasSize, setCanvasSize] = useState({ width: 300, height: 150 });

  // Update canvas size based on container
  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth - 40; // Padding
        const width = Math.min(containerWidth, 520);
        const height = Math.min(width * 0.35, 180);
        setCanvasSize({ width, height });
      }
    };

    updateSize();
    window.addEventListener("resize", updateSize);
    return () => window.removeEventListener("resize", updateSize);
  }, []);

  // Handle signature end
  const handleEnd = useCallback(() => {
    if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
      setIsEmpty(false);
      const dataUrl = sigCanvas.current.toDataURL("image/png");
      onEnd(dataUrl);
    }
  }, [onEnd]);

  // Handle clear
  const handleClear = useCallback(() => {
    if (sigCanvas.current) {
      sigCanvas.current.clear();
      setIsEmpty(true);
      onClear();
    }
  }, [onClear]);

  return (
    <SignatureCanvasWrapper ref={containerRef} $hasSignature={!isEmpty}>
      {/* Header */}
      <SignatureHeader>
        <SignatureTitle>
          <span className="icon">✍️</span>
          {TEXTS.CONTRACT.SIGNATURE.TITLE}
        </SignatureTitle>
        <SignatureStatus $signed={!isEmpty}>
          <span className="status-icon">{isEmpty ? "⏳" : "✓"}</span>
          {isEmpty ? "ממתין לחתימה" : "נחתם"}
        </SignatureStatus>
      </SignatureHeader>

      {/* Canvas */}
      <SignaturePad>
        <SignatureCanvasLib
          ref={sigCanvas}
          canvasProps={{
            width: canvasSize.width,
            height: canvasSize.height,
            style: {
              background: isEmpty ? "#FAFBFC" : "white",
              touchAction: "none",
              cursor: "crosshair",
              boxShadow: isEmpty
                ? "none"
                : "inset 0 0 0 1px rgba(30, 142, 62, 0.2)",
              borderRadius: "8px",
              transition: "all 0.3s ease",
            },
          }}
          backgroundColor={isEmpty ? "#FAFBFC" : "white"}
          penColor="#1a365d"
          minWidth={1.5}
          maxWidth={3.5}
          velocityFilterWeight={0.7}
          onEnd={handleEnd}
        />

        {isEmpty && (
          <SignaturePlaceholder>
            <PlaceholderIcon>✍️</PlaceholderIcon>
            <PlaceholderText>
              {TEXTS.CONTRACT.SIGNATURE.PLACEHOLDER}
            </PlaceholderText>
            <PlaceholderHint>
              <MouseIcon />
              {TEXTS.CONTRACT.SIGNATURE.HINT}
            </PlaceholderHint>
          </SignaturePlaceholder>
        )}
      </SignaturePad>

      {/* Footer */}
      <SignatureFooter>
        <FooterHint>
          <PenIcon />
          {isEmpty ? "גרור את העכבר או האצבע לחתימה" : "החתימה נשמרה"}
        </FooterHint>

        <ClearButton type="button" onClick={handleClear} disabled={isEmpty}>
          <TrashIcon />
          {TEXTS.CONTRACT.SIGNATURE.CLEAR}
        </ClearButton>
      </SignatureFooter>
    </SignatureCanvasWrapper>
  );
}

export default SignatureCanvas;
